import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, Settings, Plus, GripVertical, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const { refreshSettings } = useTheme();
  const [loading, setLoading] = useState(false);
  const [settingsData, setSettingsData] = useState({
    theme: 'light',
    site_title: 'Таролог-Астролог',
    site_description: '',
    admin_email: '',
    enabled_themes: ['light', 'mystical'],
  });

  const allThemes = [
    { value: 'light', label: '☀️ Светлая', category: 'Основные темы' },
    { value: 'mystical', label: '🌙 Мистическая', category: 'Основные темы' },
    { value: 'winter', label: '❄️ Зима', category: 'Времена года' },
    { value: 'spring', label: '🌸 Весна', category: 'Времена года' },
    { value: 'summer', label: '☀️ Лето', category: 'Времена года' },
    { value: 'autumn', label: '🍂 Осень', category: 'Времена года' },
    { value: 'mercury', label: '☿ Меркурий', category: 'Планеты' },
    { value: 'venus', label: '♀ Венера', category: 'Планеты' },
    { value: 'mars', label: '♂ Марс', category: 'Планеты' },
    { value: 'jupiter', label: '♃ Юпитер', category: 'Планеты' },
    { value: 'saturn', label: '♄ Сатурн', category: 'Планеты' },
    { value: 'uranus', label: '♅ Уран', category: 'Планеты' },
    { value: 'neptune', label: '♆ Нептун', category: 'Планеты' },
    { value: 'pluto', label: '♇ Плутон', category: 'Планеты' },
  ];

  const toggleTheme = (themeValue) => {
    const enabled = settingsData.enabled_themes || [];
    if (enabled.includes(themeValue)) {
      // Don't allow disabling if it's the only theme
      if (enabled.length > 1) {
        setSettingsData({
          ...settingsData,
          enabled_themes: enabled.filter(t => t !== themeValue)
        });
      } else {
        toast.error('Должна быть хотя бы одна активная тема');
      }
    } else {
      setSettingsData({
        ...settingsData,
        enabled_themes: [...enabled, themeValue]
      });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettingsData(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, settingsData);
      await refreshSettings();
      toast.success('Настройки сохранены');
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 py-12">
      <div className="container mx-auto max-w-4xl">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <h1 className="text-4xl font-bold" style={{ color: 'var(--text-primary)' }}>
              <Settings className="inline mr-2" size={36} />
              Настройки
            </h1>
          </div>
          <Button onClick={handleSave} disabled={loading} className="btn-primary" data-testid="save-settings-button">
            <Save className="mr-2" size={18} />
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>

        <div className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Основные Настройки</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Название Сайта
                </label>
                <Input
                  data-testid="site-title-input"
                  value={settingsData.site_title}
                  onChange={(e) => setSettingsData({ ...settingsData, site_title: e.target.value })}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Описание Сайта
                </label>
                <Textarea
                  data-testid="site-description-textarea"
                  value={settingsData.site_description}
                  onChange={(e) => setSettingsData({ ...settingsData, site_description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Email Администратора (для уведомлений)
                </label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  value={settingsData.admin_email || ''}
                  onChange={(e) => setSettingsData({ ...settingsData, admin_email: e.target.value })}
                  placeholder="admin@example.com"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Тема Оформления</CardTitle>
            </CardHeader>
            <CardContent>
              <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                Выберите тему по умолчанию
              </label>
              <Select value={settingsData.theme} onValueChange={(value) => setSettingsData({ ...settingsData, theme: value })}>
                <SelectTrigger data-testid="theme-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allThemes
                    .filter(t => (settingsData.enabled_themes || []).includes(t.value))
                    .reduce((acc, theme) => {
                      const lastCategory = acc[acc.length - 1];
                      if (!lastCategory || lastCategory.category !== theme.category) {
                        acc.push({ category: theme.category, themes: [theme] });
                      } else {
                        lastCategory.themes.push(theme);
                      }
                      return acc;
                    }, [])
                    .map((group, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 && <div className="px-2 py-1 text-xs font-semibold text-gray-500 mt-2">{group.category}</div>}
                        {idx === 0 && <div className="px-2 py-1 text-xs font-semibold text-gray-500">{group.category}</div>}
                        {group.themes.map(theme => (
                          <SelectItem key={theme.value} value={theme.value}>{theme.label}</SelectItem>
                        ))}
                      </React.Fragment>
                    ))
                  }
                </SelectContent>
              </Select>
              <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                Пользователи могут переключать темы с помощью кнопки в шапке сайта.
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Настройка Формы Обратной Связи</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Заголовок Формы
                </label>
                <Input
                  value={settingsData.contact_form_title || 'Свяжитесь со Мной'}
                  onChange={(e) => setSettingsData({ ...settingsData, contact_form_title: e.target.value })}
                  placeholder="Свяжитесь со Мной"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Подзаголовок (необязательно)
                </label>
                <Input
                  value={settingsData.contact_form_subtitle || ''}
                  onChange={(e) => setSettingsData({ ...settingsData, contact_form_subtitle: e.target.value })}
                  placeholder="Опишите подзаголовок..."
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Тема письма
                </label>
                <Input
                  value={settingsData.contact_form_subject || 'Новое сообщение с сайта'}
                  onChange={(e) => setSettingsData({ ...settingsData, contact_form_subject: e.target.value })}
                  placeholder="Новое сообщение с сайта"
                />
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Эта тема будет использоваться в email уведомлениях
                </p>
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Надпись поля "Имя"
                </label>
                <Input
                  value={settingsData.contact_form_name_label || 'Ваше имя'}
                  onChange={(e) => setSettingsData({ ...settingsData, contact_form_name_label: e.target.value })}
                  placeholder="Ваше имя"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Надпись поля "Email"
                </label>
                <Input
                  value={settingsData.contact_form_email_label || 'Email'}
                  onChange={(e) => setSettingsData({ ...settingsData, contact_form_email_label: e.target.value })}
                  placeholder="Email"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Надпись поля "Сообщение"
                </label>
                <Input
                  value={settingsData.contact_form_message_label || 'Ваше сообщение'}
                  onChange={(e) => setSettingsData({ ...settingsData, contact_form_message_label: e.target.value })}
                  placeholder="Ваше сообщение"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Текст кнопки
                </label>
                <Input
                  value={settingsData.contact_form_button_text || 'Отправить'}
                  onChange={(e) => setSettingsData({ ...settingsData, contact_form_button_text: e.target.value })}
                  placeholder="Отправить"
                />
              </div>

              <div className="border-t pt-4 mt-4" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>Поля формы</h3>
                  <Button
                    onClick={() => {
                      const fields = settingsData.contact_form_fields || [];
                      setSettingsData({
                        ...settingsData,
                        contact_form_fields: [...fields, {
                          id: crypto.randomUUID(),
                          type: 'text',
                          label: 'Новое поле',
                          required: false
                        }]
                      });
                    }}
                    size="sm"
                    variant="outline"
                  >
                    <Plus size={16} className="mr-1" />
                    Добавить поле
                  </Button>
                </div>

                <div className="space-y-3">
                  {(settingsData.contact_form_fields || []).map((field, index) => (
                    <div key={field.id || index} className="p-3 rounded border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-secondary)' }}>
                      <div className="flex gap-3 items-start">
                        <div className="mt-2 cursor-move text-gray-400">
                          <GripVertical size={16} />
                        </div>

                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs mb-1 block opacity-70">Тип поля</label>
                            <Select
                              value={field.type}
                              onValueChange={(value) => {
                                const fields = [...(settingsData.contact_form_fields || [])];
                                fields[index] = { ...fields[index], type: value };
                                // Add icon for social types if missing
                                if (['telegram', 'whatsapp', 'instagram'].includes(value) && !fields[index].icon) {
                                  fields[index].icon = value === 'telegram' ? 'Send' : value === 'whatsapp' ? 'MessageCircle' : 'Instagram';
                                }
                                setSettingsData({ ...settingsData, contact_form_fields: fields });
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Текст (строка)</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="tel">Телефон</SelectItem>
                                <SelectItem value="textarea">Текст (абзац)</SelectItem>
                                <SelectItem value="select">Выпадающий список</SelectItem>
                                <SelectItem value="telegram">Telegram</SelectItem>
                                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                <SelectItem value="instagram">Instagram</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-xs mb-1 block opacity-70">Название поля</label>
                            <Input
                              value={field.label}
                              onChange={(e) => {
                                const fields = [...(settingsData.contact_form_fields || [])];
                                fields[index] = { ...fields[index], label: e.target.value };
                                setSettingsData({ ...settingsData, contact_form_fields: fields });
                              }}
                              className="h-8"
                              placeholder="Например: Ваш Telegram"
                            />
                          </div>

                          {field.type === 'select' && (
                            <div className="col-span-2">
                              <label className="text-xs mb-1 block opacity-70">Опции (через запятую)</label>
                              <Input
                                value={(field.options || []).join(', ')}
                                onChange={(e) => {
                                  const fields = [...(settingsData.contact_form_fields || [])];
                                  fields[index] = { ...fields[index], options: e.target.value.split(',').map(s => s.trim()) };
                                  setSettingsData({ ...settingsData, contact_form_fields: fields });
                                }}
                                className="h-8"
                                placeholder="Опция 1, Опция 2, Опция 3"
                              />
                            </div>
                          )}

                          <div className="col-span-2 flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => {
                                  const fields = [...(settingsData.contact_form_fields || [])];
                                  fields[index] = { ...fields[index], required: e.target.checked };
                                  setSettingsData({ ...settingsData, contact_form_fields: fields });
                                }}
                                className="rounded border-gray-300"
                              />
                              Обязательное
                            </label>

                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto"
                              onClick={() => {
                                const fields = [...(settingsData.contact_form_fields || [])];
                                fields.splice(index, 1);
                                setSettingsData({ ...settingsData, contact_form_fields: fields });
                              }}
                            >
                              <Trash2 size={14} className="mr-1" />
                              Удалить
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(!settingsData.contact_form_fields || settingsData.contact_form_fields.length === 0) && (
                    <div className="text-center p-4 border border-dashed rounded opacity-60">
                      Нет дополнительных полей. Будут использованы стандартные поля (Имя, Email, Сообщение).
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Настройка Формы Записи на Консультацию</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Заголовок Формы
                </label>
                <Input
                  value={settingsData.consultation_form_title || 'Запись на Консультацию'}
                  onChange={(e) => setSettingsData({ ...settingsData, consultation_form_title: e.target.value })}
                  placeholder="Запись на Консультацию"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Подзаголовок (необязательно)
                </label>
                <Input
                  value={settingsData.consultation_form_subtitle || ''}
                  onChange={(e) => setSettingsData({ ...settingsData, consultation_form_subtitle: e.target.value })}
                  placeholder="Выберите удобное время..."
                />
              </div>
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Текст кнопки
                </label>
                <Input
                  value={settingsData.consultation_form_button_text || 'Забронировать'}
                  onChange={(e) => setSettingsData({ ...settingsData, consultation_form_button_text: e.target.value })}
                  placeholder="Забронировать"
                />
              </div>

              <div className="border-t pt-4 mt-4" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>Дополнительные поля формы</h3>
                  <Button
                    onClick={() => {
                      const fields = settingsData.consultation_form_fields || [];
                      setSettingsData({
                        ...settingsData,
                        consultation_form_fields: [...fields, {
                          id: crypto.randomUUID(),
                          type: 'text',
                          label: 'Новое поле',
                          required: false
                        }]
                      });
                    }}
                    size="sm"
                    variant="outline"
                  >
                    <Plus size={16} className="mr-1" />
                    Добавить поле
                  </Button>
                </div>

                <div className="space-y-3">
                  {(settingsData.consultation_form_fields || []).map((field, index) => (
                    <div key={field.id || index} className="p-3 rounded border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-secondary)' }}>
                      <div className="flex gap-3 items-start">
                        <div className="mt-2 cursor-move text-gray-400">
                          <GripVertical size={16} />
                        </div>

                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs mb-1 block opacity-70">Тип поля</label>
                            <Select
                              value={field.type}
                              onValueChange={(value) => {
                                const fields = [...(settingsData.consultation_form_fields || [])];
                                fields[index] = { ...fields[index], type: value };
                                setSettingsData({ ...settingsData, consultation_form_fields: fields });
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Текст (строка)</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="tel">Телефон</SelectItem>
                                <SelectItem value="date">Дата</SelectItem>
                                <SelectItem value="time">Время</SelectItem>
                                <SelectItem value="textarea">Текст (абзац)</SelectItem>
                                <SelectItem value="select">Выпадающий список</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-xs mb-1 block opacity-70">Название поля</label>
                            <Input
                              value={field.label}
                              onChange={(e) => {
                                const fields = [...(settingsData.consultation_form_fields || [])];
                                fields[index] = { ...fields[index], label: e.target.value };
                                setSettingsData({ ...settingsData, consultation_form_fields: fields });
                              }}
                              className="h-8"
                              placeholder="Например: Желаемая дата"
                            />
                          </div>

                          {field.type === 'select' && (
                            <div className="col-span-2">
                              <label className="text-xs mb-1 block opacity-70">Опции (через запятую)</label>
                              <Input
                                value={(field.options || []).join(', ')}
                                onChange={(e) => {
                                  const fields = [...(settingsData.consultation_form_fields || [])];
                                  fields[index] = { ...fields[index], options: e.target.value.split(',').map(s => s.trim()) };
                                  setSettingsData({ ...settingsData, consultation_form_fields: fields });
                                }}
                                className="h-8"
                                placeholder="Опция 1, Опция 2, Опция 3"
                              />
                            </div>
                          )}

                          <div className="col-span-2 flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => {
                                  const fields = [...(settingsData.consultation_form_fields || [])];
                                  fields[index] = { ...fields[index], required: e.target.checked };
                                  setSettingsData({ ...settingsData, consultation_form_fields: fields });
                                }}
                                className="rounded border-gray-300"
                              />
                              Обязательное
                            </label>

                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto"
                              onClick={() => {
                                const fields = [...(settingsData.consultation_form_fields || [])];
                                fields.splice(index, 1);
                                setSettingsData({ ...settingsData, consultation_form_fields: fields });
                              }}
                            >
                              <Trash2 size={14} className="mr-1" />
                              Удалить
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(!settingsData.consultation_form_fields || settingsData.consultation_form_fields.length === 0) && (
                    <div className="text-center p-4 border border-dashed rounded opacity-60">
                      Нет дополнительных полей. Будут использованы стандартные поля из календаря.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Управление Темами</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                Выберите темы, которые будут доступны пользователям для переключения
              </p>
              <div className="space-y-4">
                {['Основные темы', 'Времена года', 'Планеты'].map(category => (
                  <div key={category}>
                    <h4 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{category}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {allThemes
                        .filter(t => t.category === category)
                        .map(theme => (
                          <label
                            key={theme.value}
                            className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-opacity-50"
                            style={{
                              background: (settingsData.enabled_themes || []).includes(theme.value)
                                ? 'var(--button-bg)'
                                : 'var(--bg-secondary)',
                              color: (settingsData.enabled_themes || []).includes(theme.value)
                                ? 'var(--button-text)'
                                : 'var(--text-primary)'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={(settingsData.enabled_themes || []).includes(theme.value)}
                              onChange={() => toggleTheme(theme.value)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{theme.label}</span>
                          </label>
                        ))
                      }
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Настройка Email Уведомлений</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }} className="mb-3">
                Для отправки email уведомлений необходимо добавить SendGrid API ключ в .env файл:
              </p>
              <pre className="p-3 rounded text-sm" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                SENDGRID_API_KEY=your_sendgrid_api_key{`\n`}
                SENDER_EMAIL=your_verified_email@domain.com
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;